import React, { useState } from "react";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, downloadBlob } from "@/lib/api";
import { toast } from "sonner";

// Historical import: one CSV row = one physical piece. Source is fixed to
// "Opening Balance / Self" - the backend creates the OB master bill automatically.
export function OpeningPiecesImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file) { toast.error("Choose a CSV / Excel file"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/inventory/master-bills/import-opening", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} piece(s) itemized under ${data.bill?.bill_no}`);
      onImported?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setFile(null); setResult(null); } onOpenChange(o); }}>
      <DialogContent data-testid="opening-import-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import Opening Pieces</DialogTitle>
          <DialogDescription>Bring in the stock you already own. Every row is ONE physical piece; the source is set to <span className="font-medium text-foreground">Opening Balance / Self</span> automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 text-sm">
            <p className="font-medium mb-1">Columns</p>
            <p className="font-mono text-xs break-words">sku, gross_weight_g, net_weight_g, diamond_carat, purity, unit_cost, notes</p>
            <p className="text-xs text-muted-foreground mt-2">Only <span className="font-mono">sku</span> and <span className="font-mono">gross_weight_g</span> are required. The SKU must already exist as a Product Master.</p>
            <Button data-testid="opening-template-button" variant="secondary" size="sm" className="gap-1 mt-3" onClick={() => downloadBlob("/inventory/master-bills/import-opening/template", "opening_pieces_template.csv").catch(() => toast.error("Download failed"))}><Download className="h-3.5 w-3.5" /> Download template</Button>
          </div>
          <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-[#F7F0E6] p-6 cursor-pointer hover:border-[hsl(var(--primary))] transition-colors">
            <FileSpreadsheet className="h-7 w-7 text-[hsl(var(--primary))]" />
            <span className="text-sm font-medium">{file ? file.name : "Click to choose CSV / Excel file"}</span>
            <input data-testid="opening-import-file-input" type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {result && (
            <div data-testid="opening-import-result" className="rounded-lg border border-border p-3 text-sm">
              <div className="text-[#1F5B2E] font-medium">Created {result.created} piece(s) · Bill <span className="font-mono">{result.bill?.bill_no}</span></div>
              {result.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-destructive list-disc ml-4 max-h-32 overflow-y-auto">
                  {result.errors.map((er, i) => <li key={`${er}-${i}`}>{er}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button data-testid="run-opening-import-button" onClick={run} disabled={busy || !file} className="gap-2"><Upload className="h-4 w-4" /> {busy ? "Importing..." : "Import Pieces"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
